import Reveal from '@/components/feature/Reveal';
import { SectionWrapper, SectionHeading } from '@/components/feature/SectionHeading';
import type { AsaBulletSectionData, BulletVariant } from '@/pages/services/ai-security/content';

const bulletIconByVariant: Record<BulletVariant, string> = {
  problem: 'ri-close-circle-line',
  symptom: 'ri-question-line',
  check: 'ri-check-double-line',
};

const iconColorByVariant: Record<BulletVariant, string> = {
  problem: 'text-[#f472b6]',
  symptom: 'text-[#a78bfa]',
  check: 'text-[#a855f7]',
};

export default function BulletSection({ section }: { section: AsaBulletSectionData }) {
  const icon = bulletIconByVariant[section.variant];
  const iconColor = iconColorByVariant[section.variant];

  return (
    <SectionWrapper className={section.alt ? 'bg-[#12121f]' : ''}>
      <SectionHeading eyebrow={section.eyebrow} title={section.title} />

      {section.intro && section.intro.length > 0 && (
        <div className="mt-8 max-w-3xl mx-auto text-center space-y-4">
          {section.intro.map((p, i) => (
            <Reveal key={i} delay={i * 60}>
              <p className="text-[#a0a0b0] text-base leading-loose">{p}</p>
            </Reveal>
          ))}
        </div>
      )}

      <div className="mt-12 md:mt-16 grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto">
        {section.bullets.map((b, i) => (
          <Reveal key={b} delay={i * 40}>
            <div className="bg-[#1a1a2e] border border-[#2a2a3e] rounded-2xl p-5 flex items-start gap-4 group h-full transition-all duration-300 hover:-translate-y-1 hover:border-[#7c3aed] hover:shadow-[0_0_20px_rgba(124,58,237,0.15)]">
              <span className={`w-8 h-8 shrink-0 flex items-center justify-center ${iconColor}`}>
                <i className={`${icon} text-xl`}></i>
              </span>
              <p className="text-sm text-[#c0c0d0] leading-relaxed">{b}</p>
            </div>
          </Reveal>
        ))}
      </div>

      {section.closing && (
        <Reveal className="mt-10 max-w-3xl mx-auto">
          <p className="text-center text-base md:text-lg text-[#e8e8f0] font-medium leading-relaxed">
            {section.closing}
          </p>
        </Reveal>
      )}

      {section.note && (
        <Reveal className="mt-10 max-w-3xl mx-auto">
          <div className="flex items-start gap-3 bg-[#1a1a2e] border border-[#2a2a3e] rounded-2xl p-5">
            <span className="w-6 h-6 shrink-0 rounded-full bg-[#7c3aed]/20 text-[#a855f7] flex items-center justify-center mt-0.5">
              <i className="ri-information-line text-sm"></i>
            </span>
            <p className="text-xs md:text-sm text-[#a0a0b0] leading-relaxed">{section.note}</p>
          </div>
        </Reveal>
      )}
    </SectionWrapper>
  );
}